import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventService } from '@shared/services/event.service';
import { EventModel } from '@shared/models/event.model';
import { switchMap, map } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { PublicPageTitleService } from '@public/app/services/public-page-title.service';
import { AttendeesService } from '@shared/services/attendees.service';
import { AuthService } from '@shared/services/auth.service';
import { LoadingIndicatorService } from '@public/app/services/loading-indicator.service';
import { ToastService } from '@public/app/services/toast.service';

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.scss'],
})
export class EventDetailsComponent implements OnInit, OnDestroy {
  event$: Observable<EventModel>;
  subscriptions: Subscription[] = [];
  pageTitle = '';

  showRegistrationModal = false;
  selectedEvent: EventModel | null = null;
  isRegisteredModal = false;
  userName = '';
  userEmail = '';

  constructor(
    private route: ActivatedRoute,
    private eventService: EventService,
    private publicPageTitleService: PublicPageTitleService,
    private attendeesService: AttendeesService,
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private toastService: ToastService
  ) {
    this.publicPageTitleService.setPageTitle(this.pageTitle);
  }

  ngOnInit(): void {
    this.event$ = this.route.paramMap.pipe(
      switchMap(params => {
        const eventId = params.get('id');
        if (!eventId) {
          return of(null);
        }
        return this.eventService.getFutureApprovedEvents().pipe(
          map(events => {
            return events.find(e => e.id === eventId);
          })
        );
      })
    );

    this.subscriptions.push(
      this.event$.subscribe(ev => {
        if (ev) {
          this.publicPageTitleService.setPageTitle(ev.name);
        }
      })
    );
  }

  openRegistrationModal(event: EventModel): void {
    this.selectedEvent = event;
    this.isRegisteredModal = !!event.isRegistered;
    this.showRegistrationModal = true;

    const currentUser = this.authService.getCurrentUser();
    this.userName = currentUser ? currentUser.name : 'Ismeretlen felhasználó';
    this.userEmail = currentUser ? currentUser.email : 'Ismeretlen felhasználó';
  }

  cancelModal(): void {
    this.showRegistrationModal = false;
    this.selectedEvent = null;
    this.isRegisteredModal = false;
  }

  confirmRegistration(): void {
    this.loadingIndicatorService.showLoading();
    if (!this.selectedEvent || !this.authService.getCurrentUser()) {
      console.log('Missing feature: handle no user or event');
      this.loadingIndicatorService.hideLoading();
      return;
    }
    const eventCopy = { ...this.selectedEvent };
    this.attendeesService
      .goToEvent(this.authService.getCurrentUser().id, eventCopy.id, eventCopy.organiserId, this.userName)
      .subscribe({
        next: () => {
          eventCopy.isRegistered = true;
          this.toastService.showSuccess('Sikeres jelentkezés');
        },
        error: err => {
          eventCopy.isRegistered = false;
          const errorMsg = this.translateErrorMessage(err?.message);
          this.loadingIndicatorService.hideLoading();
          this.toastService.showError(errorMsg);
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading();
        },
      });
  }

  onUnregister(event: EventModel): void {
    this.loadingIndicatorService.showLoading();
    if (!this.authService.getCurrentUser()) {
      console.log('Missing feature: handle no user');
      this.loadingIndicatorService.hideLoading();
      return;
    }
    this.attendeesService
      .cancelApplicationToEvent(this.authService.getCurrentUser().id, event.id, event.organiserId)
      .subscribe({
        next: () => {
          event.isRegistered = false;
          this.toastService.showSuccess('Eseményről való lejelentkezés sikeres');
        },
        error: err => {
          const errorMsg = this.translateErrorMessage(err?.message);
          this.loadingIndicatorService.hideLoading();
          this.toastService.showError(errorMsg);
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading();
        },
      });
  }

  private translateErrorMessage(message: string): string {
    if (message === 'No user name was provided.') {
      return 'Nincs felhasználónév megadva.';
    } else if (message === 'Already applied for this event') {
      return 'Már regisztráltál erre az eseményre.';
    } else if (message === 'Event is not exists') {
      return 'Ez az esemény nem létezik.';
    } else if (message === 'No more users can apply for this event. It has reached the max.') {
      return 'Az esemény elérte a maximális résztvevők számát.';
    } else if (message === 'No such an organization') {
      return 'Nincs ilyen szervezet.';
    } else if (
      message === 'only authenticated users can go to an Event ' ||
      message === 'only authenticated users can remove application of an Event'
    ) {
      return 'Csak bejelentkezett felhasználók használhatják ezt a funkciót.';
    } else {
      console.log('Missing feature: more error translation');
      return 'Ismeretlen hiba történt.';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}